// src/App.jsx
import React, { Suspense } from "react";
import {
  HashRouter, Routes, Route, useLocation, Navigate, Link,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ChatProvider } from "./context/ChatContext";
import { VideoCallProvider } from "./context/VideoCallContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import ContactUs from './pages/ContactUs';
import AdminAndroidApp from './pages/admin/AdminAndroidApp';

/* ========== CORE PAGES ========== */
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Notifications from "./pages/Notifications";
import Chat from "./pages/Chat";
import Admin from "./pages/Admin";
import Members from "./pages/Members";
import Media from "./pages/Media";
import Toolkit from './pages/Toolkit';

/* ========== KEPT PAGES ========== */
import Board from "./pages/Board";
import About from "./pages/About";

/* ========== AUTH PAGES ========== */
import Login from "./pages/Login";
import Register from "./pages/Register";
import ApprovalPending from "./pages/ApprovalPending";

/* ================================================================== */
/* Routes where the app chrome (Sidebar / Footer) should be hidden     */
/* ================================================================== */

const AUTH_ROUTES = ["/login", "/register", "/approval-pending"];

const isAuthRoute = (pathname) =>
  AUTH_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(`${r}/`),
  );

/* ================================================================== */
/* Page Fallback Loader                                                */
/* ================================================================== */

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto border-4 border-orange border-t-transparent rounded-full animate-spin" />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
        Loading…
      </p>
    </div>
  </div>
);

/* ================================================================== */
/* ScrollManager                                                       */
/* ================================================================== */

const ScrollManager = () => {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 140;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 80);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
};

/* ================================================================== */
/* 404                                                                 */
/* ================================================================== */

const NotFound = () => (
  <div className="min-h-[80vh] flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-orange/10 flex items-center justify-center">
        <i
          className="fas fa-compass text-orange text-3xl"
          aria-hidden="true"
        />
      </div>
      <h1 className="text-6xl font-bold text-orange mb-2">404</h1>
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
        Page not found
      </h2>
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        The page you're looking for doesn't exist or has been moved.
      </p>

      <div className="flex flex-wrap justify-center gap-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium transition active:scale-95"
        >
          <i className="fas fa-home" aria-hidden="true" /> Home
        </Link>
        <Link
          to="/board"
          className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-5 py-2.5 rounded-lg text-sm font-medium transition active:scale-95"
        >
          <i className="fas fa-users" aria-hidden="true" /> Board
        </Link>
        <Link
          to="/about"
          className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-5 py-2.5 rounded-lg text-sm font-medium transition active:scale-95"
        >
          <i className="fas fa-circle-info" aria-hidden="true" /> About
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-5 py-2.5 rounded-lg text-sm font-medium transition active:scale-95"
        >
          <i className="fas fa-envelope" aria-hidden="true" /> Contact
        </Link>
      </div>
    </div>
  </div>
);

/* ================================================================== */
/* Error Boundary                                                      */
/* ================================================================== */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("App Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="text-center p-8 max-w-md">
            <i
              className="fas fa-exclamation-triangle text-5xl text-red-500 mb-4"
              aria-hidden="true"
            />
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6 break-words">
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() =>
                  this.setState({ hasError: false, error: null })
                }
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition active:scale-95"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg transition active:scale-95"
              >
                <i className="fas fa-redo mr-2" aria-hidden="true" /> Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ================================================================== */
/* AppShell                                                            */
/* ================================================================== */

const AppShell = () => {
  const { pathname } = useLocation();
  const hideChrome = isAuthRoute(pathname);

  return (
    <>
      <Navbar />

      <div
        className={
          hideChrome
            ? "min-h-[calc(100vh-4rem)]"
            : "flex min-h-[calc(100vh-4rem)]"
        }
      >
        {!hideChrome && <Sidebar />}

        <main className="flex-1 min-w-0">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ========== PUBLIC ========== */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/approval-pending" element={<ApprovalPending />} />

              {/* ========== PUBLIC CONTENT PAGES ========== */}
              <Route path="/board" element={<Board />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<ContactUs />} />

              {/* ========== MEDIA / TOOLKIT ========== */}
              <Route path="/media" element={<Media />} />
              <Route path="/toolkit" element={<Toolkit />} />

              {/* ========== AUTH-ONLY ========== */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/:uid"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* ========== APPROVED USERS ONLY ========== */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute roles={["member", "board", "admin"]}>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute roles={["member", "board", "admin"]}>
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/members"
                element={
                  <ProtectedRoute roles={["member", "board", "admin"]}>
                    <Members />
                  </ProtectedRoute>
                }
              />

              {/* ========== ADMIN ========== */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/android-app"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AdminAndroidApp />
                  </ProtectedRoute>
                }
              />

              {/* ========== LEGACY REDIRECTS ========== */}
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/member" element={<Navigate to="/" replace />} />
              <Route
                path="/awards"
                element={<Navigate to="/about#awards" replace />}
              />
              <Route
                path="/award"
                element={<Navigate to="/about#awards" replace />}
              />

              {/* ========== 404 (must be last) ========== */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>

      {!hideChrome && <Footer />}
    </>
  );
};

/* ================================================================== */
/* App                                                                 */
/* ================================================================== */

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ChatProvider>
          <VideoCallProvider>
            <HashRouter>
              <ScrollManager />
              <AppShell />
            </HashRouter>
          </VideoCallProvider>
        </ChatProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;